import type { Position } from "./primitives";

/** Degrees carry at most this many decimal places: about 10 centimetres. */
export const POSITION_DECIMALS = 6;

/** Truncates a position to `POSITION_DECIMALS` decimal places, the most a position may carry. */
export function truncatePosition([longitude, latitude]: readonly [number, number]): Position {
  return [truncateDegrees(longitude), truncateDegrees(latitude)];
}

function truncateDegrees(value: number): number {
  const truncated = Number(value.toFixed(POSITION_DECIMALS + 1).slice(0, -1));
  return truncated === 0 ? 0 : truncated;
}

/** How far up the flat map a latitude sits, in degrees of Mercator. */
export const mercY = (lat: number) =>
  (Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)) * 180) / Math.PI;

/** The latitude a height up the flat map sits at, the inverse of `mercY`. */
export const latOfMercY = (y: number) =>
  (Math.atan(Math.exp((y * Math.PI) / 180)) * 360) / Math.PI - 90;

/* ── On the Earth ────────────────────────────────────────────────── */

const EARTH_RADIUS_KM = 6371.0088;

export function distanceKmOnEarth([lon1, lat1]: Position, [lon2, lat2]: Position) {
  const rad = Math.PI / 180;
  const a =
    Math.sin(((lat2 - lat1) * rad) / 2) ** 2 +
    Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(((lon2 - lon1) * rad) / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Where a place `east` and `north` kilometres from a point lies, as an anchored line gives its
 * points: on the flat of the Earth round the point, so a shape keeps its form and size on the ground
 * wherever the point goes.
 */
export function offsetOnEarth(
  [lon, lat]: Position,
  [east, north]: readonly [number, number],
): Position {
  const kmPerDegree = (EARTH_RADIUS_KM * Math.PI) / 180;
  const across = kmPerDegree * Math.max(0.01, Math.cos((lat * Math.PI) / 180));
  return [lon + east / across, lat + north / kmPerDegree];
}

/** A circle on the Earth as a ring of points. */
export function circleRing([lon, lat]: Position, radiusKm: number, sides = 72): Position[] {
  const rad = Math.PI / 180;
  const d = radiusKm / EARTH_RADIUS_KM;
  const ring: Position[] = [];
  for (let k = 0; k <= sides; k++) {
    const bearing = (2 * Math.PI * k) / sides;
    const lat2 = Math.asin(
      Math.sin(lat * rad) * Math.cos(d) + Math.cos(lat * rad) * Math.sin(d) * Math.cos(bearing),
    );
    const lon2 =
      lon * rad +
      Math.atan2(
        Math.sin(bearing) * Math.sin(d) * Math.cos(lat * rad),
        Math.cos(d) - Math.sin(lat * rad) * Math.sin(lat2),
      );
    ring.push([lon2 / rad, lat2 / rad]);
  }
  return ring;
}
